import { novaClient } from "@/lib/trpc";

// The generic user-update route accepts many user fields. This hook's name is a
// contract, and the narrowed type makes it enforceable. Nothing unrelated can
// ride through a mutation named "location".
interface LocationData {
  location?: string;
}

interface LocationVariables {
  data: LocationData;
  stafferId: number;
  userId: number;
}

// Caches are invalidated by userId rather than written directly: the sheet
// profile is keyed on (userId, jobId, city, state) and several entries can exist
// for one user, so there is no single input to setData against.
export function useUpdateSetLocation() {
  const novaClientUtils = novaClient.useUtils();

  const mutation = novaClient.internal.user.active.update.useMutation({
    onSuccess: (_updatedUser, { userId }) => {
      void novaClientUtils.internal.user.active.getCandidacySheetData.invalidate(
        { userId },
      );
      // Location edits don't land in the paper trail yet (the trail's USER
      // field set excludes location). Defensive invalidation so the timeline
      // catches up on its own the day nova starts trailing them.
      void novaClientUtils.internal.user.paperTrail.citationsForUser.invalidate(
        {
          userId,
        },
      );
    },
  });

  return {
    ...mutation,
    mutate: (
      variables: LocationVariables,
      options?: Parameters<typeof mutation.mutate>[1],
    ) => {
      mutation.mutate(variables, options);
    },
    mutateAsync: (
      variables: LocationVariables,
      options?: Parameters<typeof mutation.mutateAsync>[1],
    ) => mutation.mutateAsync(variables, options),
  };
}
